import React, { useState } from "react";
import { categories } from "../data/categoriesData";
import CategoryModal from "./CategoryModal";

// Categories grid. Ad Films now lives above Work (dedicated FeaturedFilms
// section), so the Work grid shows: Food · Products · Events · Corporate Films.
const WORK_IDS = ["food", "products", "events", "corporate-films"];

export const Categories = () => {
  const works = categories.filter((c) => WORK_IDS.includes(c.id));
  const [activeId, setActiveId] = useState(null);
  const active = works.find((c) => c.id === activeId) || null;

  return (
    <section
      id="work"
      data-testid="categories-section"
      className="relative bg-black py-20 sm:py-28 lg:py-32"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        {/* Header + manifesto */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-16 sm:mb-20">
          <div className="md:col-span-5">
            <h2 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]">
              Four <em className="italic font-medium">disciplines</em>,
              <br />
              one obsession.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-white/60 text-base sm:text-lg font-light leading-relaxed max-w-lg">
              Every project finds a home in one of four categories. Each
              holds both photographs and films, shot by the same hands, the
              same eyes, the same restraint.
            </p>
          </div>
        </div>

        {/* Manifesto strip */}
        <ol
          data-testid="work-manifesto"
          className="mb-14 sm:mb-20 border-y border-white/10 divide-y divide-white/10"
        >
          {works.map((c, i) => (
            <li
              key={c.id}
              className="group py-5 sm:py-7 grid grid-cols-12 items-baseline gap-4 cursor-pointer hover:bg-white/[0.03] transition-colors"
              onClick={() => setActiveId(c.id)}
              data-testid={`manifesto-row-${c.id}`}
            >
              <div className="col-span-9 sm:col-span-8 font-serif text-white text-2xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-none">
                We {manifestoVerb(c.id)}{" "}
                <em className="italic font-medium">{manifestoNoun(c.id)}</em>
                <span className="text-white/30">.</span>
              </div>
              <div className="col-span-3 sm:col-span-3 eyebrow text-white/40 hidden sm:block">
                {(() => {
                  const n = c.projects.filter((p) => (p.photos?.length || 0) + (p.videos?.length || 0) > 0).length;
                  return n === 0 ? "Coming soon" : `${n} project${n > 1 ? "s" : ""}`;
                })()}
              </div>
              <div className="col-span-3 sm:col-span-1 flex justify-end">
                <span className="text-white/40 group-hover:text-white transition-colors text-lg sm:text-xl">
                  →
                </span>
              </div>
            </li>
          ))}
        </ol>

        {/* Tile grid (visual) - smaller, tighter, no staggered offset */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5">
          {works.map((cat, i) => (
            <button
              key={cat.id}
              onClick={() => setActiveId(cat.id)}
              data-testid={`category-tile-${cat.id}`}
              className="cat-tile group relative overflow-hidden text-left focus:outline-none focus:ring-1 focus:ring-white/40 aspect-[3/4]"
            >
              <img
                src={cat.cover}
                alt={cat.title}
                className="cat-img absolute inset-0 w-full h-full object-cover img-noise"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80 group-hover:opacity-65 transition-opacity duration-700" />

              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-1.5 border border-white/40 px-2 py-1 text-[9px] tracking-[0.3em] uppercase text-white/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                Enter <span>→</span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5">
                <h3 className="font-serif italic text-white text-2xl sm:text-3xl lg:text-4xl font-light tracking-tighter leading-none">
                  {cat.title}.
                </h3>
                <div className="mt-2 flex items-end justify-between gap-3">
                  <p className="text-white/70 text-xs sm:text-sm font-light max-w-[18ch]">
                    {cat.tagline}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {active && (
        <CategoryModal category={active} onClose={() => setActiveId(null)} />
      )}
    </section>
  );
};

function manifestoVerb(id) {
  switch (id) {
    case "food":
      return "plate";
    case "products":
      return "sculpt";
    case "events":
      return "hold";
    case "corporate-films":
      return "direct";
    default:
      return "make";
  }
}

function manifestoNoun(id) {
  switch (id) {
    case "food":
      return "food";
    case "products":
      return "objects";
    case "events":
      return "rooms";
    case "corporate-films":
      return "brand films";
    default:
      return "work";
  }
}

export default Categories;
